import { motion, useSpring } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { ArrowRight } from "lucide-react";

const CometTransition = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const isMobile = useIsMobile();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isInSection, setIsInSection] = useState(false);

  // Smooth spring animation for cursor following
  const springConfig = { damping: 25, stiffness: 150, mass: 0.5 };
  const mouseX = useSpring(0, springConfig);
  const mouseY = useSpring(0, springConfig);

  useEffect(() => {
    if (isMobile) return; // Disable mouse tracking on mobile
    
    const handleMouseMove = (e: MouseEvent) => {
      if (!sectionRef.current) return;
      
      const rect = sectionRef.current.getBoundingClientRect();
      const isInBounds = 
        e.clientY >= rect.top && 
        e.clientY <= rect.bottom &&
        e.clientX >= rect.left &&
        e.clientX <= rect.right;
      
      setIsInSection(isInBounds);
      
      if (isInBounds) {
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        setMousePosition({ x, y });
        mouseX.set(x);
        mouseY.set(y);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [mouseX, mouseY, isMobile]);

  // Hide dark background on mobile - make transparent
  return (
    <section 
      ref={sectionRef}
      className="relative min-h-[70vh] my-0 overflow-hidden md:cursor-none"
      style={{
        background: isMobile 
          ? "transparent" 
          : "linear-gradient(180deg, hsl(229, 45%, 6%) 0%, hsl(245, 40%, 8%) 100%)"
      }}
    >
      {isInSection && !isMobile && (
        <>
          {/* Main comet head - smaller size */}
          <motion.div
            className="absolute w-6 h-6 pointer-events-none -ml-3 -mt-3 z-50"
            style={{
              left: mouseX,
              top: mouseY,
            }}
          >
            {/* Core glow - deep violet */}
            <motion.div
              className="absolute inset-0 rounded-full"
                style={{
                  background: "radial-gradient(circle, #9b5de5 0%, #b794f6 40%, transparent 70%)",
                  filter: "blur(2px)",
                }}
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.9, 1, 0.9],
              }}
              transition={{
                duration: 0.8,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            
            {/* Bright center */}
            <motion.div
              className="absolute inset-1 rounded-full bg-white"
              style={{
                boxShadow: "0 0 15px #9b5de5, 0 0 30px #b794f6",
              }}
            />
            
            {/* Outer glow ring */}
            <motion.div
              className="absolute -inset-2 rounded-full"
              style={{
                background: "radial-gradient(circle, rgba(155, 93, 229, 0.5) 0%, transparent 60%)",
                filter: "blur(8px)",
              }}
              animate={{
                scale: [1, 1.3, 1],
              }}
              transition={{
                duration: 1,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </motion.div>

          {/* Trailing light effect - multiple segments for smooth trail */}
          {Array.from({ length: 15 }).map((_, i) => {
            const delay = i * 0.015;
            const scale = 1 - (i / 20);
            
            return (
              <motion.div
                key={`trail-${i}`}
                className="absolute rounded-full pointer-events-none"
                style={{
                  left: mouseX,
                  top: mouseY,
                  width: `${20 - i}px`,
                  height: `${20 - i}px`,
                  marginLeft: `${-(20 - i) / 2}px`,
                  marginTop: `${-(20 - i) / 2}px`,
                  background: `radial-gradient(circle, rgba(155, 93, 229, ${0.6 - i * 0.04}) 0%, rgba(183, 148, 246, ${0.3 - i * 0.02}) 50%, transparent 100%)`,
                  filter: "blur(4px)",
                }}
                transition={{
                  type: "spring",
                  damping: 25 + i * 2,
                  stiffness: 150 - i * 5,
                  mass: 0.5 + i * 0.05,
                }}
              />
            );
          })}

          {/* Sparkle particles that emit from comet */}
          {Array.from({ length: 8 }).map((_, i) => {
            const angle = (i / 8) * Math.PI * 2;
            const distance = 20 + Math.random() * 30;
            
            return (
              <motion.div
                key={`sparkle-${i}`}
                className="absolute rounded-full pointer-events-none"
                style={{
                  left: mouseX,
                  top: mouseY,
                  width: "3px",
                  height: "3px",
                  background: i % 2 === 0 ? "#ffffff" : "#b794f6",
                  boxShadow: `0 0 6px ${i % 2 === 0 ? "#ffffff" : "#9b5de5"}`,
                }}
                animate={{
                  x: [0, Math.cos(angle) * distance],
                  y: [0, Math.sin(angle) * distance],
                  opacity: [1, 0],
                  scale: [1, 0],
                }}
                transition={{
                  duration: 0.6,
                  repeat: Infinity,
                  delay: i * 0.1,
                  ease: "easeOut",
                }}
              />
            );
          })}

          {/* Soft glow that follows cursor */}
          <motion.div
            className="absolute pointer-events-none"
            style={{
              left: mouseX,
              top: mouseY,
              width: "200px",
              height: "200px",
              marginLeft: "-100px",
              marginTop: "-100px",
              background: "radial-gradient(circle, rgba(155, 93, 229, 0.2) 0%, rgba(183, 148, 246, 0.1) 40%, transparent 70%)",
              filter: "blur(30px)",
            }}
            transition={{
              type: "spring",
              damping: 30,
              stiffness: 100,
            }}
          />
        </>
      )}


      {/* Ambient shimmer particles in background - Reduced on mobile */}
      {Array.from({ length: isMobile ? 10 : 20 }).map((_, i) => {
        const randomX = Math.random() * 100;
        const randomY = Math.random() * 100;
        const randomDelay = Math.random() * 3;
        const size = Math.random() * 2 + 1;
        
        return (
          <motion.div
            key={`ambient-${i}`}
            className="absolute rounded-full pointer-events-none"
            style={{
              left: `${randomX}%`,
              top: `${randomY}%`,
              width: `${size}px`,
              height: `${size}px`,
              background: i % 2 === 0 ? "#b794f6" : "#d4bbf8",
              boxShadow: `0 0 ${size * 3}px #9b5de5`,
            }}
            animate={{
              scale: [0, 1, 1, 0],
              opacity: [0, 0.6, 0.4, 0],
            }}
            transition={{
              duration: 3,
              delay: randomDelay,
              ease: "easeInOut",
              repeat: Infinity,
              repeatDelay: 2,
            }}
          />
        );
      })}

      {/* Call to Action Content - moved from CallToAction component */}
      <div className="relative z-10 max-w-5xl mx-auto text-center px-4 py-24">
        {/* Main Heading */}
        <motion.h2 
          className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          Take the Next Step in Your{" "}
          <span className="text-gradient">Dance Journey</span>
        </motion.h2>

        {/* Subheading */}
        <motion.p 
          className="text-lg md:text-xl text-muted-foreground mb-12 max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          Join our community and discover the art of connection through movement
        </motion.p>

        {/* CTA Button */}
        <motion.div 
          className="flex justify-center"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <a 
            href="https://klauyros.artoverse.club/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <button className="elegant-cta-button group">
              <span className="elegant-cta-text">
                Enter the Studio
                <ArrowRight className="inline-block ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </span>
            </button>
          </a>
            </motion.div>
      </div>
    </section>
  );
};

export default CometTransition;
